import { useQuery } from '@tanstack/react-query';
import { Alert, Spin } from 'antd';
import queryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { WorkPlace as WorkPlaceResponse } from '@/DTOS/WorkPlace/Response/WorkPlace';
import { updateProps } from '../../contextRedux/WorkPlace/WorkPlaceModel';
import { setWorkPlaces } from '../../contextRedux/WorkPlace/WorkPlaceStore';
import { AnimateButton } from '../../customComponents/button/CustomButton';
import WorkPlaceService from '../../services/workPlace/WorkPlaceService';
import { RootState } from '../../store';
import WorkPlaceDrawer from './WorkPlaceDrawer/WorkPlaceDrawer';
import WorkPlaceTable from './WorkPlaceTable/WorkPlaceTable';

const WorkPlace: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state.models?.workPlace?.props?.searchTerm);
  const skip = useSelector((state: RootState) => state.models?.workPlace?.props?.skip);
  const take = useSelector((state: RootState) => state.models?.workPlace?.props?.take);
  const refresh = useSelector((state: RootState) => state.models?.workPlace?.props?.refresh);

  const [modalInformation, setModalInformation] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<WorkPlaceResponse | null>(null);
  const [isParamsInitialized, setIsParamsInitialized] = useState<boolean>(false);

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });

  useEffect(() => {
    const queryParams = queryString.parse(location?.search);

    const termInUrl = (queryParams?.term as string) || '';
    const pageInUrl = Number(queryParams?.page) || 1;
    const pageSizeInUrl = Number(queryParams?.pageSize) || take;

    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsParamsInitialized(true);
  }, [dispatch, location?.search, take]);

  useEffect(() => {
    if (isParamsInitialized) {
      const queryParams = queryString.parse(location?.search);
      const currentPage = Math.floor(skip / take) + 1;

      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage?.toString() !== previousParamsRef.current.page ||
        take?.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage?.toString() : undefined,
          pageSize: take !== 10 ? take?.toString() : undefined,
        };

        const newSearch = queryString.stringify(params);
        navigate(`${location?.pathname}?${newSearch}`, { replace: true });

        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage?.toString(),
          pageSize: take?.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isParamsInitialized, navigate, location?.pathname, location?.search]);

  const {
    data: _dates,
    isFetching: isWorkPlacesLoading,
    isError: isErrorOnFetchWorkPlaces,
    error: workPlacesError,
    refetch,
  } = useQuery({
    queryKey: ['workPlaces', searchTerm, skip, take, refresh],
    queryFn: () =>
      WorkPlaceService.search({
        isAgentIncluded: true,
        isUserIncluded: true,
        isWorkPlaceItemIncluded: true,
        isCompanyIncluded: true,
        termen: searchTerm || undefined,
        skip,
        take,
      }).then((data: any) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setWorkPlaces(data?.response));
        return data?.response;
      }),
    enabled: isParamsInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  // If searchTerm is cleared, ensure refetch
  useEffect(() => {
    if (isParamsInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isParamsInitialized, refetch]);

  const handleModalInformationOpen = (record: WorkPlaceResponse) => {
    setSelectedRecord(record);
    setModalInformation(true);
  };

  const handleModalInformationClose = () => {
    setModalInformation(false);
  };

  if (isWorkPlacesLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isErrorOnFetchWorkPlaces) {
    return <Alert message={(workPlacesError as Error)?.message || 'Error fetching data'} type='error' />;
  }

  return (
    <>
      <h1 style={{ marginBottom: 50 }}>Punct De Lucru</h1>
      <AnimateButton onClick={() => window.open(`/administration/workPlace/new`, '_blank')}>Adauga</AnimateButton>
      <WorkPlaceDrawer
        modalInformation={modalInformation}
        handleModalInformationClose={handleModalInformationClose}
        selectedRecord={selectedRecord}
      />
      <WorkPlaceTable handleModalInformationOpen={handleModalInformationOpen} />
    </>
  );
};

export default WorkPlace;
